import { useState, useEffect } from "react";
import { useApi } from "../../../utils/Api";
import Translate from "../../../utils/Translate";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Preloader from "../../Preloader";
import { Autocomplete, Box, Grid, IconButton, TextField, Tooltip } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useSnackbar } from "notistack";
import InfoIcon from "@mui/icons-material/Info";
import { DatePicker } from "@mui/x-date-pickers";
import { getCurrentDateMask, getCurrentInputFormat } from "../../../utils/dateTimeFormat";
import { accessKeys, hasSomeAccess } from "../../../utils/userAccess";

const CloneCourse = ({ fromCourseId, oldFromDate, oldToDate, onClose }) => {
  const [courseTemplates, setCourseTemplates] = useState([]);
  const [selectedCourseTemplateId, setSelectedCourseTemplateId] = useState(null);
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);

  const [isLoading, setIsLoading] = useState();

  const api = useApi();
  const navigate = useNavigate();
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();

  useEffect(() => {
    setFromDate(new Date(oldFromDate));
    setToDate(new Date(oldToDate));
  }, []);

  useEffect(() => {
    const getCourseTemplates = async () => {
      await GetCourseTemplates();
    };
    getCourseTemplates();
  }, []);

  async function GetCourseTemplates() {
    setIsLoading(true);
    let response = null;
    try {
      response = await api.fetch(`${import.meta.env.VITE_MAIN_URL}courses/templates/grouped`, false, "GET");
      if (response.isSuccessful) {
        const latestTranslation = Translate.get("LatestCourseTemplates");
        const allTranslation = Translate.get("AllCourseTemplates");
        const transformedCourseTemplates = response.latest
          .map((x) => ({ group: latestTranslation, courseTemplate: x }))
          .concat(
            response.all.map((x) => ({
              group: allTranslation,
              courseTemplate: x,
            })),
          );

        setCourseTemplates(transformedCourseTemplates);
      }
    } finally {
      setIsLoading(false);
    }
  }

  async function PostCourseClone() {
    setIsLoading(true);
    let response = null;
    try {
      let reqObj = {
        courseTemplateId: selectedCourseTemplateId,
        fromDate: fromDate,
        toDate: toDate,
      };
      response = await api.fetch(`${import.meta.env.VITE_MAIN_URL}courses/${fromCourseId}/clone`, reqObj, "POST");
      if (response.isSuccessful) {
        const successMessage = enqueueSnackbar(Translate.get("CloneSuccessful"), {
          variant: "success",
          autoHideDuration: 12000,
          onClick: () => closeSnackbar(successMessage),
        });
        onClose();
        navigate(`/dashboard/course/${response.courseId}`);
      }
    } finally {
      setIsLoading(false);
    }
  }

  async function handleCreate() {
    await PostCourseClone();
  }

  if (isLoading) {
    return (
      <div>
        <Preloader />
      </div>
    );
  }

  return (
    <Dialog
      open={true}
      onClose={onClose}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
      maxWidth="sm"
      fullWidth={true}
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center" width="100%">
          {Translate.get("CloneCourse")}{" "}
          <Tooltip title={Translate.get("CloneCourseInfo")}>
            <IconButton>
              <InfoIcon color="primary" />
            </IconButton>
          </Tooltip>
        </Box>
      </DialogTitle>
      <DialogContent>
        <div>
          <Autocomplete
            fullWidth
            value={
              courseTemplates.find((opt) => opt.courseTemplate.courseTemplateId === selectedCourseTemplateId) || null
            }
            options={courseTemplates}
            groupBy={(x) => x.group}
            getOptionLabel={(option) => {
              return option.courseTemplate.courseShortName;
            }}
            renderOption={(props, option) => (
              <Box component="li" {...props}>
                <div className="twoRowOptions">
                  <div>{option.courseTemplate.courseShortName}</div>
                  <div>{option.courseTemplate.description}</div>
                </div>
              </Box>
            )}
            onChange={(event, newValue) => {
              setSelectedCourseTemplateId(newValue ? newValue.courseTemplate.courseTemplateId : null);
            }}
            renderInput={(params) => (
              <TextField {...params} style={{ marginTop: "1em" }} required label={Translate.get("NewCourseType")} />
            )}
          />
        </div>

        <Grid container spacing={2} style={{ marginTop: "2em" }}>
          <Grid size={{ xs: 12, md: 6 }}>
            <DatePicker
              displayWeekNumber={hasSomeAccess(accessKeys.swedishCompany, accessKeys.isMASystem)}
              label={Translate.get("FromDate")}
              mask={getCurrentDateMask()}
              format={getCurrentInputFormat()}
              value={fromDate}
              onChange={(newValue) => setFromDate(newValue)}
              slotProps={{
                textField: {
                  fullWidth: true,
                  variant: "outlined",
                },
              }}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <DatePicker
              displayWeekNumber={hasSomeAccess(accessKeys.swedishCompany, accessKeys.isMASystem)}
              label={Translate.get("ToDate")}
              mask={getCurrentDateMask()}
              format={getCurrentInputFormat()}
              value={toDate}
              onChange={(newValue) => setToDate(newValue)}
              slotProps={{
                textField: {
                  fullWidth: true,
                  variant: "outlined",
                  error: !!fromDate && !!toDate && fromDate >= toDate,
                  helperText: !!fromDate && !!toDate && fromDate >= toDate ? Translate.get("EndDateSmall") : "",
                },
              }}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{Translate.get("Cancel")}</Button>
        <Button onClick={handleCreate} disabled={selectedCourseTemplateId == null || fromDate >= toDate}>
          {Translate.get("Create")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CloneCourse;
